import Button from "../../../components/Comon/Button/Button";
import HealthConcernRate from "../../../components/Survey/HealthConcernRate/HealthConcernRate";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import VideoPlayer from "../../../components/Survey/VideoPlayer/VideoPlayer";
import ScoreChangePromptModal from "../../../components/Survey/ScoreChangePromptModal";
import "./WellnessSurveySections.scss";

export default function WellnessSurveyMetricsScreen({
                                                        formData,
                                                        setFormData = () => {},
                                                        errorsData,
                                                        settingsData,
                                                        clinicData,
                                                        themeColor,
                                                        handleMetricsNextScreen = () => {},
                                                        handleMetricsPreviousScreen = () => {},
    previousSurvey
                                                    })
{

    const { i18n } = useTranslation();

    const metrics = [
        {
            title:settingsData?.healthMetric1Title,
            question:settingsData?.healthMetric1Question,
            lowScale:settingsData?.healthMetric1LowScale,
            highScale:settingsData?.healthMetric1HighScale,
            key:'healthMetric1',
        },
        {
            title:settingsData?.healthMetric2Title,
            question:settingsData?.healthMetric2Question,
            lowScale:settingsData?.healthMetric2LowScale,
            highScale:settingsData?.healthMetric2HighScale,
            key:'healthMetric2',
        },
        {
            title:settingsData?.healthMetric3Title,
            question:settingsData?.healthMetric3Question,
            lowScale:settingsData?.healthMetric3LowScale,
            highScale:settingsData?.healthMetric3HighScale,
            key:'healthMetric3',
        },
        {
            title:settingsData?.healthMetric4Title,
            question:settingsData?.healthMetric4Question,
            lowScale:settingsData?.healthMetric4LowScale,
            highScale:settingsData?.healthMetric4HighScale,
            key:'healthMetric4',
        },
        {
            title:settingsData?.healthMetric5Title,
            question:settingsData?.healthMetric5Question,
            lowScale:settingsData?.healthMetric5LowScale,
            highScale:settingsData?.healthMetric5HighScale,
            key:'healthMetric5',
        },
        {
            title:settingsData?.healthMetric6Title,
            question:settingsData?.healthMetric6Question,
            lowScale:settingsData?.healthMetric6LowScale,
            highScale:settingsData?.healthMetric6HighScale,
            key:'healthMetric6',
        }
    ];


    const showLastScore = settingsData?.healthLastScore ?? false;
    const healthMetricScaleVariant =
        settingsData?.healthMetricScaleDesign === "green_to_red"
            ? "green_to_red"
            : "red_to_green";
    const redToGreenScale = [
        "rgb(166, 1, 35)",
        "rgb(212, 2, 40)",
        "rgb(237, 11, 10)",
        "rgb(244, 56, 4)",
        "rgb(249, 117, 2)",
        "rgb(248, 179, 0)",
        "rgb(250, 220, 1)",
        "rgb(205, 223, 15)",
        "rgb(140, 213, 33)",
        "rgb(69, 188, 55)",
        "rgb(23, 170, 64)",
    ];
    const greenToRedScale = [...redToGreenScale].reverse();

    const getLastTimeRateColor = (rate) => {
        const colorIndex = Math.max(0, Math.min(10, Number(rate)));
        const activeScale =
            healthMetricScaleVariant === "green_to_red"
                ? greenToRedScale
                : redToGreenScale;
        return activeScale[colorIndex];
    };

    const renderLastScore = (rate) => {
        const text = i18n.t("youRatedItLastTime", { rate });
        if (rate === null || rate === undefined || rate === "") {
            return text;
        }
        const rateString = String(rate);
        const ratePosition = String(text).indexOf(rateString);

        if (ratePosition === -1) return text;

        const beforeRate = text.slice(0, ratePosition);
        const afterRate = text.slice(ratePosition + rateString.length);

        return (
            <>
                {beforeRate}
                <span
                    style={{
                        display: "inline-block",
                        padding: "4px",
                        textAlign: "center",
                        minWidth:'20px',
                        borderRadius: "4px",
                        color: "#ffffff",
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: 1,
                        backgroundColor: getLastTimeRateColor(rate),
                        textShadow: '0px 0px 3px rgba(0,0,0,0.31)'
                    }}
                >
                    {rateString}
                </span>
                {afterRate}
            </>
        );
    };


    function validateMetrics() {
        const missingTitles = [];

        (metrics ?? []).forEach((metric) => {
            const key = metric?.key;
            const title = metric?.title ?? key;

            // Treat 0 as a valid answered value
            const value = formData?.[key];
            const answered = value !== undefined && value !== null && value !== '';

            if (!answered) {
                missingTitles.push(title);
            }
        });

        const missingLabel = missingTitles.length
            ? `${i18n.t('pleaseAnswer')}: ${missingTitles.join(', ')}`
            : '';

        return {
            isValid: missingTitles.length === 0,
            missing: missingTitles,
            missingLabel,
        };
    }

    const validation = validateMetrics();

    themeColor = themeColor ?? '#61849D';

    const [videoSize, setVideoSize] = useState({ width: 600, height: 405 });
    const [scorePromptShown, setScorePromptShown] = useState(false);
    const scorePromptShownRef = useRef(false);
    const [scorePrompt, setScorePrompt] = useState({
        open: false,
        index: null,
        previousRate: null,
        currentRate: null,
    });

    useEffect(() => {
        setScorePromptShown(false);
        scorePromptShownRef.current = false;
        setScorePrompt({
            open: false,
            index: null,
            previousRate: null,
            currentRate: null,
        });
    }, []);

    const closeScorePrompt = () => {
        setScorePrompt((prev) => ({ ...prev, open: false }));
    };

    const changeScoreAnswer = () => {
        if (scorePrompt.index === null || scorePrompt.index === undefined) {
            closeScorePrompt();
            return;
        }

        const metricKey = metrics?.[scorePrompt.index]?.key;
        if (metricKey) {
            setFormData({ ...formData, [metricKey]: null });
        }
        closeScorePrompt();
    };

    const handleRateChange = (index, key, value) => {
        setFormData({ ...formData, [key]: value });

        if (scorePromptShownRef.current) {
            return;
        }

        const previousRate = Number(previousSurvey?.[key]);
        const currentRate = Number(value);
        const hasValidRates =
            Number.isFinite(previousRate) && Number.isFinite(currentRate);
        const hasNegativeSwing = hasValidRates && previousRate - currentRate >= 2;

        if (hasNegativeSwing) {
            scorePromptShownRef.current = true;
            setScorePromptShown(true);
            setScorePrompt({
                open: true,
                index,
                previousRate,
                currentRate,
            });
        }
    };

    const adjustVideoSize = () => {
        if (window.screen.width < 641) {
            setVideoSize({
                width: 297,
                height: 200,
            });
        } else {
            setVideoSize({
                width: 600,
                height: 405,
            });
        }
    };
    useEffect(() => {
        adjustVideoSize();

        function handleResize() {
            adjustVideoSize();
        }

        window.addEventListener("resize", handleResize);
    }, []);



    return <div style={{
        maxWidth:'800px',
        display:'flex',
        justifyContent:'center',
        alignItems:'stretch',
        flexDirection:'column'
    }}>
        <ScoreChangePromptModal
            open={scorePrompt.open}
            previousRate={scorePrompt.previousRate}
            currentRate={scorePrompt.currentRate}
            onConfirm={closeScorePrompt}
            onChangeAnswer={changeScoreAnswer}
        />

        <div
            style={{
                width:'100%',
                display:'grid',
                gridTemplateColumns:settingsData?.healthVideoLink ? '1fr 1fr' : '1fr',
                alignItems:'center',
                justifyContent:'center',
                gap:'50px',
            }}
        >
            <div style={{
                display:'flex',
                justifyContent:'center',
                alignItems:'center',
                flexDirection:'column',
                padding:'160px 0'
            }}>
                <div className={"headline"} style={{textAlign:'center'}}>{settingsData?.healthSectionTitle}</div>
                <p style={{ margin: 0, textAlign:'center' }} className="text">
                    {settingsData?.healthDescription}
                </p>

                <div
                    style={{ display:'flex', justifyContent:'flex-start',alignItems:'center',marginBottom:'100px'}}
                >
                    <a href={'#FORM'} style={{textDecoration:'none'}}>
                        <div className={"presentContainer"}>
                            <Button
                                color={themeColor}
                                mode={"small"}
                                text={i18n.t("startSurvey")}
                            />
                        </div>
                    </a>
                </div>
            </div>

            {settingsData?.healthVideoLink &&
                <div style={{marginTop:'30px'}}>
                    <VideoPlayer
                        url={settingsData?.healthVideoLink}
                        themeColor={themeColor}
                        videoSize={videoSize}
                        id="SurveyHomePage"
                    />
                </div>
            }
        </div>


        <div
            id={'FORM'}
            className={"wellnessQuestionCard"}
            style={{
                width:'100%',
                display:'flex',
                justifyContent:'center',
                alignItems:'stretch',
                flexDirection:'column',
            }}
        >
            {
                metrics?.map((item,index) => {
                    return <div
                        key={index}
                        className={"wellnessQuestionItem"}
                        style={{
                            width:'100%',
                            display:'flex',
                            justifyContent:'center',
                            alignItems:'flex-start',
                            flexDirection:'column',
                            gap:'0px',
                        }}
                    >
                        <div className={"helloDiv"} style={{ border: 'none', marginBottom:'8px' }}>{item?.title}</div>
                        <div className={"headline"} style={{width:'100%', fontSize:'40px', lineHeight:'54px',marginTop:0, textAlign:'left', marginBottom:(showLastScore && previousSurvey)? '10px' : '30px'}}>{item?.question}</div>

                        {
                            (showLastScore && previousSurvey) && <div className={"healthDescription"} style={{marginBottom:'30px'}}>
                                {renderLastScore(previousSurvey?.[item?.key])}
                            </div>
                        }

                        <HealthConcernRate
                            newScale={true}
                            scaleVariant={healthMetricScaleVariant}
                            rate={formData?.[item?.key]}
                            hasSpecificRate={false}
                            themeColor={themeColor}
                            i18n={i18n}
                            leftOption={item?.lowScale}
                            rightOption={item?.highScale}
                            setRate={(value) => handleRateChange(index, item?.key, value)}

                        />
                    </div>
                })
            }
        </div>


        <div
            style={{ display:'flex', flexDirection:'column', justifyContent:'center',alignItems:'center',marginBottom:'100px', marginTop:'60px'}}
        >
            <div className={"presentContainer"} style={{width:'360px'}} onClick={() => validation.isValid ? handleMetricsNextScreen() : null}>
                <Button
                    color={themeColor}
                    mode={"small"}
                    text={i18n.t("next")}
                    disabled={!validation.isValid}
                />
            </div>

            {/*{*/}
            {/*    !validation.isValid ? <div style={{textAlign:'center', color:"red", marginTop:'10px'}}>{validation.missingLabel}</div> : null*/}
            {/*}*/}

            <div style={{color:'gray',textAlign:'center',textDecoration:'underline',marginTop:'15px', cursor:'pointer'}} onClick={() => handleMetricsPreviousScreen() }>
                {i18n.t("previous")}
            </div>
        </div>
    </div>
}
